import {
  normalizeMemberPermissions,
  fullMemberPermissions,
  canChangeRecord,
  type MemberFeature,
} from '~/utils/memberPermissions'
import { computed } from 'vue'
import { useUserStore } from '~/stores/userStore'
import { useOrgStore } from '~/stores/orgStore'

export const usePermissions = () => {
  const userStore = useUserStore()
  const orgStore = useOrgStore()

  const isLicenseAdmin = computed(() => {
    return userStore.user?.licenseAdmin === true
  })

  const isAdmin = computed(() => {
    return userStore.user?.orgRole === 'Admin'
  })

  const isMember = computed(() => {
    return userStore.user?.orgRole === 'Member'
  })

  const canEditOrgSettings = computed(() => {
    return isLicenseAdmin.value || isAdmin.value
  })

  const canInviteUsers = computed(() => {
    return isLicenseAdmin.value || isAdmin.value
  })

  const canModifyAdministration = computed(() => {
    return isLicenseAdmin.value
  })

  // Customer-facing vocabulary: the license admin is presented as the "Owner".
  // Internally (API, database) the role stays 'Admin' + licenseAdmin=true.
  const isOwner = isLicenseAdmin

  const memberPermissions = computed(() =>
    isOwner.value || isAdmin.value
      ? fullMemberPermissions()
      : normalizeMemberPermissions(orgStore.selected.permissions),
  )
  const canViewIntakes = computed(() => memberPermissions.value.intakes.view)
  const canViewAudits = computed(() => memberPermissions.value.audits.view)
  const canCreateIntakes = computed(
    () => memberPermissions.value.intakes.create,
  )
  const canCreateAudits = computed(() => memberPermissions.value.audits.create)
  const canDeleteIntakes = computed(
    () => memberPermissions.value.intakes.delete,
  )
  const canDeleteAudits = computed(() => memberPermissions.value.audits.delete)
  const canEditRecord = (
    feature: MemberFeature,
    record?: { createdBy?: string; completedAt?: string; status?: string },
  ) =>
    canChangeRecord(
      memberPermissions.value[feature],
      userStore.user?.id,
      record,
    )
  return {
    memberPermissions,
    canViewIntakes,
    canViewAudits,
    canCreateIntakes,
    canCreateAudits,
    canDeleteIntakes,
    canDeleteAudits,
    canEditRecord,
    isLicenseAdmin,
    isOwner,
    isAdmin,
    isMember,
    canEditOrgSettings,
    canInviteUsers,
    canModifyAdministration,
  }
}

// displayRole maps a user's internal role fields to the customer-facing role name.
// The Owner is shown as "Owner" (never "Owner" and "Admin" together).
export const displayRole = (user: {
  licenseAdmin?: boolean
  role?: string
  orgRole?: string
}): string => {
  if (user?.licenseAdmin) return 'Owner'
  return user?.role || user?.orgRole || 'Member'
}
